import { useEffect, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Formik } from 'formik'

import { useRootStore } from '~/store/RootStore'
import { checkMfaBeforeLoad } from '~/utils/beforeLoad'

import {
  Button,
  Card,
  CheckBox,
  ContentWrapper,
  ErrorMsg,
  Flex,
  Form,
  FormFields,
  H6,
  Input,
  Label,
  Paragraph,
  Spacer,
} from '~/components/elements'
import { ArrowBackIosOutlineIcon } from '~/components/icons'
import { IProfileFormValues, IProfileUpdateRequest } from '~/types'
import { initialProfileFormValues } from '~/utils/initialValues'
import {
  getErrorsFromValidationError,
  IFormikProps,
  mapProfile,
  profileContactValidationSchema,
  trimPropStrings,
} from '~/components/profile/helper'
import Loading from '~/components/loading'
import { AddressFields } from '~/components/profile/AddressFields'
import { Modal } from '~/components/modal'
import { titleCase } from '~/utils/helpers'

export const Route = createFileRoute('/_authenticated/profile/edit')({
  beforeLoad: checkMfaBeforeLoad,
  component: ProfileEditPage,
})

function ProfileEditPage() {
  const navigate = useNavigate({ from: '/profile' })
  const { account } = useRootStore()
  const profile = account.getProfile

  const [isLoading, setIsLoading] = useState(true)
  const [formLoading, setFormLoading] = useState(false)
  const [initialValues, setInitialValues] = useState<IProfileFormValues>(
    initialProfileFormValues
  )
  const [showModal, setShowModal] = useState(false)
  const [modalMessage, setModalMessage] = useState<{
    title: React.ReactNode
    message: React.ReactNode
  }>({
    title: '',
    message: '',
  })

  useEffect(() => {
    if (profile && profile.contacts && profile.contacts.length) {
      const values: IProfileFormValues = mapProfile(profile)
      setInitialValues(values)
      setIsLoading(false)
    }
    return () => {
      setInitialValues({} as IProfileFormValues)
    }
  }, [profile])

  const onClickModalOk = () => {
    navigate({ to: '/profile' })
  }

  const onSubmit = async (values: IProfileFormValues) => {
    setFormLoading(true)
    const billingAddress = {
      firstName: values.addresses.billing.firstName,
      lastName: values.addresses.billing.lastName,
      preferredName: values.addresses.billing.preferredName || undefined,
      pronouns: values.addresses.billing.pronouns || undefined,
      levelNumber: values.addresses.billing.levelNumber,
      levelType: values.addresses.billing.levelType,
      lotNumber: values.addresses.billing.lotNumber,
      postCode: values.addresses.billing.postCode,
      state: values.addresses.billing.state,
      streetName: titleCase(values.addresses.billing.streetName || '').replace(
        /\s+/g,
        ' '
      ),
      streetNumber: values.addresses.billing.streetNumber,
      streetType: values.addresses.billing.streetType,
      suburb: titleCase(values.addresses.billing.suburb || '').replace(
        /\s+/g,
        ' '
      ),
      unitNumber: values.addresses.billing.unitNumber,
      unitType: values.addresses.billing.unitType,
    }

    const shippingAddress = {
      firstName: values.addresses.shipping.firstName,
      lastName: values.addresses.shipping.lastName,
      preferredName: values.addresses.shipping.preferredName || undefined,
      pronouns: values.addresses.shipping.pronouns || undefined,
      levelNumber: values.addresses.shipping.levelNumber,
      levelType: values.addresses.shipping.levelType,
      lotNumber: values.addresses.shipping.lotNumber,
      postCode: values.addresses.shipping.postCode,
      state: values.addresses.shipping.state,
      streetName: titleCase(values.addresses.shipping.streetName || '').replace(
        /\s+/g,
        ' '
      ),
      streetNumber: values.addresses.shipping.streetNumber,
      streetType: values.addresses.shipping.streetType,
      suburb: titleCase(values.addresses.shipping.suburb || '').replace(
        /\s+/g,
        ' '
      ),
      unitNumber: values.addresses.shipping.unitNumber,
      unitType: values.addresses.shipping.unitType,
    }

    let payload: IProfileUpdateRequest = {
      preferredName: values.preferredName || undefined,
      pronouns: values.pronouns || undefined,
      emailAddress: values.emailAddress,
      preferredMessageType: values.preferredMessageType || undefined,
      contactMethods: values.contactMethods || [],
      phoneHome: values.phone.home || undefined,
      phoneMobile: values.phone.mobile || undefined,
      phoneWork: values.phone.work || undefined,
      shippingAddress: trimPropStrings(shippingAddress),
      billingAddress: trimPropStrings(billingAddress),
    }

    await account
      .postUpdateProfile(payload)
      .then(() => {
        setFormLoading(false)
        setModalMessage({
          title: 'Edit Profile Success',
          message: 'Profile was successfuly updated.',
        })
        setShowModal(true)
      })
      .catch((reason) => {
        setModalMessage({
          title: 'Edit Profile Error',
          message:
            reason.message ||
            'There was an error updating the profile. Please try again later.',
        })
        setFormLoading(false)
        setShowModal(true)
      })
  }

  const validate = (values: IProfileFormValues) => {
    try {
      profileContactValidationSchema.validateSync(values, {
        abortEarly: false,
        context: { data: values },
      })
      return {}
    } catch (error) {
      return getErrorsFromValidationError(error)
    }
  }

  const Fields = ({
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
  }: IFormikProps) => {
    // Function to handle contact method change
    const handleContactMethodChange = (method: 'Email' | 'SMS') => {
      const currentContactMethods = values.contactMethods

      // Add or Remove ContactMethod
      const updatedMethods = currentContactMethods.includes(method)
        ? currentContactMethods.filter((m: string) => m !== method)
        : [...currentContactMethods, method]

      handleChange({
        target: {
          name: 'contactMethods',
          value: updatedMethods,
        },
      } as any)
    }

    return (
      <Form
        className="form-fields data-suppress"
        layout="vertical"
        onSubmit={handleSubmit}
        data-suppress
      >
        {formLoading && <Loading />}
        <Flex alignitems="center" justifycontent="flex-end">
          <Button type="button" variant="secondary" onClick={onClickModalOk}>
            Cancel
          </Button>
          &nbsp;&nbsp;&nbsp;
          <Button type="submit" variant="brand">
            Submit
          </Button>
        </Flex>

        <H6>Contact</H6>
        <Card>
          <FormFields>
            <Label>Preferred Name</Label>
            <div>
              <Input
                name="preferredName"
                type="text"
                value={values.preferredName ?? ''}
                onChange={handleChange}
                placeholder="Enter preferred name"
              />
            </div>
          </FormFields>
          <FormFields>
            <Label>Pronouns</Label>
            <div>
              <Input
                name="pronouns"
                type="text"
                value={values.pronouns ?? ''}
                onChange={handleChange}
                placeholder="Enter pronouns"
              />
            </div>
          </FormFields>
          <FormFields>
            <Label>Email Address</Label>
            <div>
              <Input
                name="emailAddress"
                type="text"
                value={values.emailAddress}
                onChange={handleChange}
                placeholder="Enter email address"
                $error={!!(errors && errors.emailAddress)}
              />
              {!!(errors && errors.emailAddress) && (
                <ErrorMsg>{errors.emailAddress}</ErrorMsg>
              )}
            </div>
          </FormFields>
          <FormFields>
            <Label>Home Phone</Label>
            <div>
              <Input
                name="phone.home"
                type="text"
                value={values.phone.home}
                onChange={handleChange}
                placeholder="Enter home phone"
                $error={!!(errors && errors['phone.home'])}
              />
              {errors && errors['phone.home'] ? (
                <ErrorMsg>{errors['phone.home']}</ErrorMsg>
              ) : (
                ''
              )}
            </div>
          </FormFields>
          <FormFields>
            <Label>Mobile Phone</Label>
            <div>
              <Input
                name="phone.mobile"
                type="text"
                value={values.phone.mobile}
                onChange={handleChange}
                placeholder="Enter mobile phone"
                $error={!!(errors && errors['phone.mobile'])}
              />
              {!!(errors && errors['phone.mobile']) && (
                <ErrorMsg>{errors && errors['phone.mobile']}</ErrorMsg>
              )}
            </div>
          </FormFields>
          <FormFields>
            <Label>Work Phone</Label>
            <div>
              <Input
                name="phone.work"
                type="text"
                value={values.phone.work}
                onChange={handleChange}
                placeholder="Enter work phone"
                $error={!!(errors && errors['phone.work'])}
              />
              {!!(errors && errors['phone.work']) && (
                <ErrorMsg>{errors && errors['phone.work']}</ErrorMsg>
              )}
              {!!(errors && errors.phone) && (
                <ErrorMsg>{errors && errors.phone}</ErrorMsg>
              )}
            </div>
          </FormFields>

          <FormFields>
            <Label>Contact Methods</Label>
            <div>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '15px' }}
              >
                <CheckBox>
                  <Input
                    type="checkbox"
                    checked={values.contactMethods?.includes('Email') || false}
                    onChange={() => handleContactMethodChange('Email')}
                    onBlur={handleBlur}
                    id="emailContactMethod"
                  />
                  <Label>Email</Label>
                </CheckBox>

                <CheckBox>
                  <Input
                    type="checkbox"
                    checked={values.contactMethods?.includes('SMS') || false}
                    onChange={() => handleContactMethodChange('SMS')}
                    onBlur={handleBlur}
                    id="smsContactMethod"
                  />
                  <Label>SMS</Label>
                </CheckBox>
              </div>
              {!!(errors && errors.contactMethods) && (
                <ErrorMsg>{errors.contactMethods}</ErrorMsg>
              )}
            </div>
          </FormFields>
        </Card>

        <H6>
          Billing Address
          <br />
          <small className="text-muted">
            This address will be used on invoices
          </small>
        </H6>
        <Card>
          <AddressFields
            type="addresses.billing"
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors}
            touched={touched}
            {...values.addresses.billing}
          />
        </Card>

        <H6>
          Shipping Address
          <br />
          <small className="text-muted">
            This address will be used to ship modems and other hardware
          </small>
        </H6>
        <Card>
          <AddressFields
            type="addresses.shipping"
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors}
            touched={touched}
            {...values.addresses.shipping}
          />
        </Card>

        {/* <H6>Preferred Communication</H6>
        <Card>
          <FormFields>
            <Label>Communication Type</Label>
            <Select
              name="preferredMessageType"
              onChange={handleChange}
              value={values.preferredMessageType}
            >
              <option value="Email">Email</option>
              <option value="SMS">SMS</option>
            </Select>
          </FormFields>
        </Card> */}
        <Flex alignitems="center" justifycontent="flex-end">
          <Button type="button" variant="secondary" onClick={onClickModalOk}>
            Discard
          </Button>
          &nbsp;&nbsp;&nbsp;
          <Button type="submit" variant="brand">
            Submit
          </Button>
        </Flex>
      </Form>
    )
  }

  return (
    <ContentWrapper>
      {isLoading && <Loading />}
      <Button
        size="small"
        variant="secondary"
        onClick={() => navigate({ to: '/profile' })}
      >
        <ArrowBackIosOutlineIcon /> Back
      </Button>

      <Spacer height="12px" />
      <Formik
        initialValues={{
          preferredName: initialValues.preferredName,
          pronouns: initialValues.pronouns,
          emailAddress: initialValues.emailAddress,
          phone: {
            home: initialValues.phone.home,
            work: initialValues.phone.work,
            mobile: initialValues.phone.mobile,
          },
          addresses: {
            billing: initialValues.addresses.billing,
            shipping: initialValues.addresses.shipping,
          },
          preferredMessageType: initialValues.preferredMessageType,
          contactMethods: initialValues.contactMethods,
        }}
        enableReinitialize={true}
        validate={validate}
        onSubmit={onSubmit}
        component={Fields}
      />
      <Modal
        open={showModal}
        title={modalMessage.title}
        hideCloseIcon
        footer={
          <>
            <Button variant="brand" onClick={onClickModalOk}>
              Ok
            </Button>
          </>
        }
      >
        <Paragraph>{modalMessage.message}</Paragraph>
      </Modal>
    </ContentWrapper>
  )
}
